import { useState } from "react";
import {
  Alert, Box, Button, CircularProgress, MenuItem, Paper, Stack, TextField, Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { stockTakesApi, warehousesApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { todayIso } from "@/shared/datetime";

/**
 * UC-M1-INV-08 — Open a new cycle count. The server snapshots SystemQty per
 * (item, batch) for every batch with non-zero on-hand at the chosen warehouse;
 * operators fill CountedQty per line on the detail page, then Post to write
 * the variance (positive deltas → ADJUSTMENT, negative deltas → WRITE_OFF).
 */
export function CreateStockTakePage() {
  const navigate = useNavigate();
  const [warehouseToCountId, setWarehouseToCountId] = useState("");
  const [documentDate, setDocumentDate] = useState(todayIso);
  const [notes, setNotes] = useState("");

  const warehouses = useQuery({
    queryKey: ["warehouses", "all"],
    queryFn: () => warehousesApi.list({ pageSize: 200 }),
  });

  const mutation = useMutation({
    meta: { successMessage: "Stock take created" },
    mutationFn: () => stockTakesApi.create({
      warehouseToCountId, documentDate,
      notes: notes || undefined,
    }),
    onSuccess: (r) => navigate(`/inventory/stocktakes/${r.id}`),
  });

  const validationError = !warehouseToCountId ? "Pick a warehouse to count." : null;

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>New stock take</Typography>
      <Paper sx={{ p: 3 }}>
        {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
        <Alert severity="info" sx={{ mb: 2 }}>
          On save the system snapshots the current system on-hand qty for every batch with
          stock at the chosen warehouse. You count the physical units on the detail page and
          post the variance once you're done.
        </Alert>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField select label="Warehouse to count *" size="small" sx={{ minWidth: 320 }}
            value={warehouseToCountId} onChange={(e) => setWarehouseToCountId(e.target.value)}>
            <MenuItem value="">— pick warehouse —</MenuItem>
            {warehouses.data?.items.map(w => (
              <MenuItem key={w.id} value={w.id}>{w.code} — {w.name}</MenuItem>
            ))}
          </TextField>
          <TextField type="date" label="Date *" size="small" InputLabelProps={{ shrink: true }}
            value={documentDate} onChange={(e) => setDocumentDate(e.target.value)} />
        </Stack>
        <TextField label="Notes" fullWidth multiline minRows={2}
          value={notes} onChange={(e) => setNotes(e.target.value)} sx={{ mb: 2 }}
          placeholder="e.g. quarterly count — main pharmacy" />

        <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
          <Button onClick={() => navigate("/inventory/stocktakes")}>Cancel</Button>
          <Button variant="contained" disabled={!!validationError || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => mutation.mutate()}>Snapshot & start count</Button>
        </Stack>
        {validationError && <Alert severity="info" sx={{ mt: 2 }}>{validationError}</Alert>}
      </Paper>
    </Box>
  );
}
